import { useEffect } from "react";

import { useTranslator } from "@eo-locale/react";

import { useRouter } from "next/router";

import Cookies from "js-cookie";

import {
  Button,
  IconButton,
  useBreakpointValue,
  FiEdit
} from "@/storybook-components";

import useUser from "@/hooks/useUser";
import { useAppSelector, useAppDispatch } from "@/redux/store/hooks";
import useUnverifiedModal from "@/hooks/useUnverfiedModal";
import {
  setTriggerPoint,
  setModalStatus
} from "@/redux/features/unverified-modal/slice";

const ActionButton = ({
  projectVisibility,
  canContribute,
  isVotingAllowed,
  allowUnverifiedParticipation,
  onOpenHandler
}): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { isUnverfiedUserLoggedIn, isRegisteredUserLoggedIn } = useUser();
  const { ideas } = useAppSelector((state) => state?.brainstormers);
  const { triggerPoint } = useAppSelector((state) => state.unverifiedModal);
  const cookieValues = Cookies.get("trigger_point");
  let cookieTriggerPoint;
  if (cookieValues) {
    cookieTriggerPoint = JSON.parse(cookieValues);
  }

  const { action } = router.query;
  const isIdeaBoardEmpty = ideas.length === 0;
  const isMobileScreen = useBreakpointValue({
    base: true,
    xl: false
  });

  const isButtonDisabled =
    ((projectVisibility === "Panelled" || projectVisibility === "Protected") &&
      !canContribute) ||
    isVotingAllowed;

  useUnverifiedModal(allowUnverifiedParticipation, (isProceedAllowed) => {
    if (isProceedAllowed) {
      dispatch(
        setTriggerPoint({ ...triggerPoint, isTriggeredPointActive: false })
      );
      if (
        triggerPoint.triggeredSource === "new_idea" ||
        cookieTriggerPoint?.triggeredSource === "new_idea"
      ) {
        onOpenHandler();
        addQueryParam();
      }
      Cookies.remove("trigger_point");
    }
  });
  const handleClick = () => {
    if (isButtonDisabled) return;
    // If user is not registered, redirect to login modal
    if (!isUnverfiedUserLoggedIn && !isRegisteredUserLoggedIn) {
      const triggerPoint = {
        triggeredSource: "new_idea",
        isTriggeredPointActive: true
      };
      Cookies.set("trigger_point", JSON.stringify(triggerPoint), {
        expires: 1,
        secure: true,
        sameSite: "Strict"
      });
      dispatch(setModalStatus("create"));
      dispatch(setTriggerPoint(triggerPoint));
    } else {
      onOpenHandler();
      addQueryParam();
    }
  };

  useEffect(() => {
    if (
      action === "new_idea" &&
      (triggerPoint.triggeredSource === "new_idea" ||
        cookieTriggerPoint?.triggeredSource === "new_idea")
    )
      onOpenHandler();
  }, [onOpenHandler, action, triggerPoint]);

  const addQueryParam = () => {
    router.query.action = "new_idea";
    router.replace(router, "", { shallow: true });
  };

  return (
    <>
      {!isIdeaBoardEmpty && (
        <>
          <IconButton
            aria-label={translate("brainstormers.newPost")}
            icon={<FiEdit />}
            w={{ base: 10, sm: 12, xl: 10 }}
            h={{ base: 10, sm: 12, xl: 10 }}
            variant="primary"
            borderRadius="50%"
            boxShadow="lg"
            onClick={handleClick}
            isDisabled={isButtonDisabled}
            hidden={!isMobileScreen}
            sx={{
              "& > svg": {
                height: 5,
                width: 5
              }
            }}
          />
          <Button
            variant="primary"
            size={{ base: "lg", xl: "md" }}
            leftIcon={<FiEdit />}
            fontWeight="semibold"
            fontSize="md"
            py={2}
            px={4}
            onClick={handleClick}
            isDisabled={isButtonDisabled}
            hidden={isMobileScreen}
          >
            {translate("brainstormers.newPost")}
          </Button>
        </>
      )}
    </>
  );
};

export default ActionButton;
